import { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { useTrans } from "@/utils/useTrans";
import { cn } from "@/libs/utils";
import { analyticsInstance } from "@/libs/analytics";
import { isPromotionLinkClick } from "@/libs/analytics/use-promotion-tracking";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/v2-components/ui/carousel";

/** `useSyncExternalStore` 로 «마운트됐다» 만 판정하기 위한 no-op 구독. */
const subscribeNothing = () => () => {};

/**
 * 캠페인 팝업. 「오늘 하루 안 보기」는 `popup-banner-store` 가 하루 기억한다.
 *
 * ```
 *   데스크탑  우하단 플로팅 카드 360 (딤 없음)   — 뒤를 계속 볼 수 있다
 *   모바일    딤 + 하단 시트                    — 좁은 화면에선 카드가 내용을 다 가린다
 * ```
 *
 * ## ⚠️ 이 컴포넌트는 **2026-09-09 까지 렌더된 적이 없었다**
 *
 * 타입·스토어·마크업이 다 있었는데 `import` 하는 곳이 0곳이었다. GTA5 기브어웨이 팝업이
 * **첫 소비처**다. 그래서 되살리며 두 가지를 고쳤다 — 둘 다 소비처가 없어서 안 드러났던
 * 것이고, 호출처가 0곳이라 바꿔도 깨질 데가 없었다:
 *
 * 1. **크기** — 종전엔 `min(90vw, (85vh - 40px) × 1280/680)` 이었다. 「1280×680 배너
 *    한 장」만 넣는다는 전제인데, 실제 팝업은 배너 + 카피 + 버튼이라 합쳐진 비율이
 *    글자 줄바꿈에 따라 변한다. 고정 비율로 폭을 정하면 내용이 넘치거나 남는다.
 *    → 평범한 모달(`max-w` + `max-h` + 넘치면 스크롤)로 바꿨다.
 * 2. **접근성** — `role`·`aria-modal`·Esc 가 없었다. 딤이 뒤를 덮는 **모바일**은
 *    `aria-modal="true"`, 딤이 없는 **데스크탑 카드**는 `false` 다 — 형제
 *    `welcome-coupon-popup` 과 같은 기준(딤 유무로 가른다).
 *
 * ⚠️ 배경 스크롤은 잠그지 않는다 — 잠그려면 스크롤바 폭 보정까지 따라와서 표면이 는다.
 * 지금은 딤을 누르면 닫히므로 갇히지 않는다.
 */
/**
 * 한 장의 캠페인 슬라이드.
 *
 * ★**「오늘 하루 안 보기」는 슬라이드마다 따로다** (작성자 확정 2026-09-10:
 * *"해당 장면의 이벤트만 영향"*). 그게 **이미 있는 데이터 모델과 맞는다** — 캠페인별로
 * 저장소가 이미 분리돼 있다(`popup-banner` / `welcome-coupon-popup`). 껍데기 전체를
 * 끄는 쪽으로 하면 세 번째 저장소를 새로 만들거나 둘 중 하나를 골라야 한다.
 */
export interface PopupSlide {
  /** 캐러셀 key. 캠페인 식별자를 쓴다. */
  key: string;
  /** 스크린리더가 읽을 이 슬라이드의 이름. */
  label: string;
  node: React.ReactNode;
  /** 「오늘 하루 안 보기」를 눌렀을 때 — **이 캠페인만** 끈다. */
  onDismissToday: () => void;
  /**
   * 셸의 「닫기」로 **전체 캐러셀이 닫히면** 이 슬라이드의 제공자에게 알린다 — 닫힌 **뒤에** 합류한
   * 슬라이드도 합류하는 즉시 한 번 받는다(그 슬라이드도 이번 방문에는 셸로 다시 뜨지 않으므로).
   *
   * 선택적이다 — GTA5 처럼 "이번 방문에 닫혔다"를 별도로 기억할 표면(미니 배지 등)이
   * 없는 캠페인은 생략한다. 셸은 캠페인별 저장소를 모르므로(제공자 소유 원칙) 이
   * 훅으로만 알린다.
   */
  onClose?: () => void;
}

interface FloatingPopupProps {
  /**
   * 보여줄 슬라이드. **호출처가 이미 걸러서** 넘긴다(캠페인별 노출 판정은 각자의 몫).
   *
   * ```
   *   0장   아무것도 안 그린다
   *   1장   점·자동넘김 없음 — 넘길 데가 없다
   *   2장+  캐러셀 + 자동넘김
   * ```
   */
  slides: PopupSlide[];
}

/**
 * `prefers-reduced-motion` 질의. **`matchMedia` 가 없을 수 있다** — jsdom 에 없고,
 * 오래된 웹뷰에도 없다. 없으면 «감소 안 함» 으로 본다(자동넘김 유지).
 *
 * ⚠️ 테스트에서만 나는 문제로 보고 스텁으로 덮으면 **실환경 취약점이 남는다** — 그래서
 * 코드에서 방어한다.
 */
const matchReduced = (): MediaQueryList | null =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;

/**
 * 자동넘김 간격. 종전 `AutoplayCarousel` 의 기본값(`delay = 5000`)에서 가져왔다 —
 * 그 컴포넌트는 #5689 가 지웠으므로(레거시 게임 화면 전용) **지금 이 값의 정본은 여기다.**
 */
const AUTOPLAY_MS = 5000;

export function FloatingPopup({ slides }: FloatingPopupProps) {
  const { t } = useTrans(["gnb"]);
  const [closed, setClosed] = useState(false);

  /**
   * ★**현재 슬라이드를 껍데기가 들고 있다** — 「오늘 하루 안 보기」가 그걸 봐야 한다.
   *
   * 값은 **보이는 표면의 캐러셀**이 올려 준다(`PopupCard` 의 `onSelect`). 숨은 표면은
   * `display:none` 이라 embla 가 선택을 바꾸지 않으므로 덮어쓰지 않는다.
   */
  const [current, setCurrent] = useState(0);

  /**
   * 「오늘 하루 안 보기」는 localStorage 에만 있어 서버는 모른다. 마운트 전에는 그리지
   * 않는다(hydration 불일치 방지).
   */
  const hydrated = useSyncExternalStore(
    subscribeNothing,
    () => true,
    () => false,
  );

  const visible = hydrated && slides.length > 0 && !closed;

  /**
   * ★**닫히면 모든 슬라이드의 제공자에게 한 번씩 알린다** — 「오늘 하루 안 보기」(현재 슬라이드만)와
   * 달리 「닫기」는 캐러셀 전체를 닫으므로, 안 본 슬라이드까지 「이번 방문 닫힘」을 반영할 기회를 준다
   * (#5761: 쿠폰은 이걸로 미니 배지 전환을 감지한다).
   *
   * 「닫힘」을 이벤트가 아니라 **상태**로 보고 커밋 뒤에 훑는다. 이유 둘:
   * - ★**닫은 뒤 합류한 슬라이드도 받는다.** 쿠폰 슬라이드는 사용자 로딩·쿠키 동의 배너가 끝나야
   *   들어온다(`useWelcomeCouponOffer` 의 `eligible`). 그 전에 다른 슬라이드만 있는 셸을 닫으면,
   *   누른 순간의 목록만 훑는 방식은 쿠폰에 알리지 못해 **팝업도 배지도 없는** 상태가 남는다.
   * - 버튼·Esc 가 같은 상태(`closed`)만 바꾸면 된다 — 렌더 중 ref 쓰기(`react-hooks/refs`)가 필요 없다.
   *
   * 슬라이드 `key` 별로 한 번만 알린다 — 부모가 `slides` 를 매 렌더 새 배열로 넘겨 이 effect 가 자주 돈다.
   */
  const notifiedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!closed) return;
    for (const s of slides) {
      if (notifiedRef.current.has(s.key)) continue;
      notifiedRef.current.add(s.key);
      s.onClose?.();
    }
  }, [closed, slides]);

  const handleClose = () => setClosed(true);

  /**
   * ★**보이는 슬라이드의 노출을 슬라이드 `key` 마다 한 번 기록한다** (연말 세일 개발 목록 D1).
   *
   * 셸에서 센다 — 데스크탑·모바일 두 표면이 **둘 다 렌더**되고 하나만 `display:none` 이라, 표면 안에서
   * 세면 숨은 쪽 슬라이드까지 노출로 잡힌다. `current` 는 보이는 표면의 캐러셀만 올려 준다(위 주석).
   * `key` 가 캠페인 식별자라(로케일 무관) 그대로 `promotion_id` 로 쓴다. 클릭은 `PopupCard` 의 셀에서 잡는다.
   */
  const viewedRef = useRef<Set<string>>(new Set());
  const currentKey = visible
    ? slides[Math.min(current, slides.length - 1)]?.key
    : undefined;
  useEffect(() => {
    if (!currentKey || viewedRef.current.has(currentKey)) return;
    viewedRef.current.add(currentKey);
    analyticsInstance?.logViewPromotion({ id: currentKey, slot: "home_popup" });
  }, [currentKey]);

  // ⚠️ 훅은 early return 위에 있어야 한다.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setClosed(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible]);

  if (!visible) return null;

  const slide = slides[Math.min(current, slides.length - 1)];

  /**
   * ★**지금 보이는 슬라이드의 캠페인만** 끈다 (작성자 확정 2026-09-10).
   *
   * ⚠️ 자동넘김과 겹치면 오조작이 된다 — 누르려는 사이 넘어가면 엉뚱한 캠페인이 하루
   * 사라진다. 그래서 `PopupCard` 가 포인터·포커스·터치에 자동넘김을 멈춘다.
   */
  /**
   * ★**팝업을 닫지 않는다 — 그 캠페인만 끈다** (작성자 확정 2026-09-11:
   * *"그 동작이 맞겠지? 개별로?"*).
   *
   * 종전엔 `setClosed(true)` 로 **전체를 닫았다.** 슬라이드가 하나뿐일 때는 차이가
   * 없었지만, 쿠폰이 들어와 2장이 되면서 문제가 된다 — GTA5 만 24시간 끄려 했는데
   * **아직 안 본 쿠폰까지** 이번 화면에서 사라진다.
   *
   * ★**여기서 목록을 걷지 않는다.** 각 제공자가 자기 저장소를 읽어 다음 렌더에
   * 자기 슬라이드를 **스스로 뺀다**(GTA5 `usePopupBannerStore` · 쿠폰
   * `closePopup()`). 그래서 `dismissedKeys` 같은 지역 집합은 **같은 일을 두 번**
   * 하는 것이고, 실제로 검증되지 않았다 — 뮤턴트로 필터를 통째로 지워도 테스트가
   * **통과**했다(2026-09-11 실측). 방어가 둘이면 서로의 뮤턴트를 가리므로 뺐다.
   *
   * ⚠️ 그래서 이 함수가 성립하는 **전제**가 있다: 제공자가 `onDismissToday` 의
   * 효과를 **자기 노출 조건에 반영**해야 한다. 안 그러면 눌러도 안 사라진다 —
   * 새 슬라이드를 붙일 때 그것을 확인할 것.
   */
  const handleDontShowToday = () => {
    slide.onDismissToday();
  };

  const bottomBar = (
    <div className="flex h-10 items-center justify-between bg-white px-4 text-sm leading-4 tracking-[0.4px]">
      <button className="text-coolgray-50" onClick={handleDontShowToday}>
        {t("Don't show again today")}
      </button>
      <button className="text-coolgray-90" onClick={handleClose}>
        {t("Close")}
      </button>
    </div>
  );

  return (
    <>
      {/**
       * Desktop — **우하단 플로팅 카드** (작성자 확정 2026-09-09).
       *
       * 종전엔 화면 가운데 딤 모달이었다. 지운 `Dk3EventStickyBanner`(#4785, 캠페인
       * 종료로 `86b516cf2` 에서 제거)가 쓰던 형태로 맞췄고, 그 뒤 **형제 팝업
       * (`welcome-coupon-popup`)과 위치·폭을 통일했다**(작성자 확정 2026-09-10).
       *
       * ```
       *   DK3(삭제본)   bottom-10 right-10  w-[360px]
       *   웰컴 쿠폰      bottom-6  right-6   w-[calc(100%-2rem)] max-w-[360px]   ← 이쪽으로
       * ```
       *
       * 폭도 그쪽 방식을 따른다 — 고정 `w-[360px]` 은 1024~1080 구간에서 여백을 먹는다.
       * 분기점은 이미 같다(`md` = `laptop` = 1024 · `tailwind.config.ts` 실측).
       *
       * ## ★딤이 없으므로 `aria-modal` 은 **false** 다
       *
       * 딤 모달이 아니면 뒤 내용이 살아 있고 클릭도 된다. `aria-modal="true"` 로 두면
       * 스크린리더에 **뒤가 없는 것처럼** 읽혀 실제와 어긋난다. 형제
       * `welcome-coupon-popup` 이 같은 이유로 `false` 다.
       *
       * Esc 는 그대로 둔다 — 닫는 수단이 하나 더 있는 것은 해롭지 않다.
       */}
      <div
        role="dialog"
        aria-modal="false"
        aria-label={slide.label}
        /**
         * ★**이 요소는 자리와 폭만 갖는다** — 그림자·라운드는 **바로 아래 자식**
         * (`overflow-hidden rounded-2xl shadow-2xl`)이 든다.
         *
         * 그림자는 **요소 사각형을 따라가므로** 여기 두면 내용보다 큰 사각형이 그려질
         * 때 빈 영역에 잔상이 남는다(작성자 지적 2026-09-10: *"쿠폰일 때 안 채워지는
         * 부분에 잔상"*). 배경을 투명하게 해도 안 없어진다 — 배경색이 아니라 그림자다.
         *
         * 지금은 `PopupCard` 뷰포트 높이가 활성 슬라이드를 따라가므로(위 판 이력 4판)
         * 그 사각형 자체가 내용과 같지만, **그림자를 안쪽에 두는 것이 그와 독립적으로
         * 옳다** — 자리 담당과 그리기 담당을 섞지 않는다.
         */
        className="fixed bottom-6 right-6 z-50 hidden w-[calc(100%-2rem)] max-w-[360px] flex-col items-stretch justify-end md:flex"
      >
        <div className="overflow-hidden rounded-2xl shadow-2xl">
          <PopupCard slides={slides} onSelect={setCurrent} />
          {bottomBar}
        </div>
      </div>

      {/* Mobile — 하단 시트 */}
      <div
        role="dialog"
        /**
         * ## ★**딤을 걷었다** — 모바일도 `aria-modal="false"` 다 (2026-09-11)
         *
         * 웰컴 쿠폰을 이 템플릿의 슬라이드로 들이면서 생긴 결정이다. 그 팝업은
         * *"딤 없이 띄운다(**기획 확정**) — 뒤 화면이 그대로 보이고 조작도 막지
         * 않는다"* 라서 종전에 **이 템플릿을 아예 재사용하지 않았다**(그 파일
         * docblock). 딤이 남아 있으면 그 계약을 깬다.
         *
         * GTA5 쪽도 딤이 필요 없다는 작성자 확정(2026-09-11)을 받아 **분기 없이**
         * 걷었다 — `dim` 프롭을 만들면 `true` 를 넘길 호출처가 0곳이라 죽은 표면이
         * 된다.
         *
         * ⚠️ 이 파일의 기존 규칙이 *"딤 유무로 `aria-modal` 을 가른다"* 이므로
         * 모바일도 `false` 다. **딤 탭으로 닫기**도 함께 없어진다 — 닫는 길은
         * 하단 바의 `Close` 와 `Esc` 로 남는다(둘 다 이미 있다).
         *
         * ⚠️ `pointer-events-none` 이 **필수**다. `inset-0` 이 전면을 덮는데 딤이
         * 투명해지면 **보이지 않는 레이어가 뒤 화면의 클릭을 다 먹는다** — 딤이
         * 있을 때는 그게 의도였지만, 없을 때는 「조작을 막지 않는다」가 계약이다.
         */
        aria-modal="false"
        aria-label={slide.label}
        className="pointer-events-none fixed inset-0 z-50 flex items-end md:hidden"
      >
        {/* 카드만 이벤트를 되살린다 — 위 `pointer-events-none` 의 짝이다. */}
        <div className="pointer-events-auto flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-2xl">
          <PopupCard slides={slides} onSelect={setCurrent} />
          {bottomBar}
        </div>
      </div>
    </>
  );
}

/**
 * 캐러셀 본체 — **표면마다 하나씩** 만든다.
 *
 * ★두 표면(데스크탑 카드 · 모바일 시트)이 embla 인스턴스를 **공유하면 안 된다.**
 * 종전엔 둘 다 같은 `setApi` 를 불러 **나중에 마운트되는 숨은 쪽**이 덮어썼고,
 * 그 api 는 `display:none` 안이라 폭이 0 이다 → 점만 바뀌고 보이는 트랙은 안 움직였다
 * (실측: `transform` 이 `matrix(1,0,0,1,0,0)` 고정 · 점은 2번인데 화면은 1번).
 *
 * 자동넘김·정지·점도 여기로 내렸다 — 보이는 인스턴스가 자기 것을 관리한다.
 * 현재 인덱스만 껍데기로 올린다(「오늘 하루 안 보기」가 그걸 본다).
 */
function PopupCard({
  slides,
  onSelect,
}: {
  slides: PopupSlide[];
  onSelect: (i: number) => void;
}) {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [paused, setPaused] = useState(false);
  const autoplay = slides.length > 1;

  const reducedMotion = useSyncExternalStore(
    (cb) => {
      const m = matchReduced();
      m?.addEventListener("change", cb);
      return () => m?.removeEventListener("change", cb);
    },
    () => matchReduced()?.matches ?? false,
    () => false,
  );

  const [current, setCurrent] = useState(0);

  /**
   * ★**껍데기 높이를 «활성 슬라이드»에 맞춘다** (#5728).
   *
   * 트랙은 `display:flex` 라 높이가 **가장 큰 슬라이드**로 정해진다. 짧은
   * 슬라이드에서는 그 차이가 빈 영역이 되고 거기서 둘이 드러났다:
   *
   * ```
   *   실측 2026-09-11 로컬 1440
   *     ?gta5-preview=1 (1장)  슬라이드 283 = 카드 283   차이 0     ← 안 보였다
   *     ?popup-demo=1   (3장)  슬라이드 490 ≠ 카드 283   차이 207
   *       → 빈 207px 에 껍데기 `shadow-2xl` 이 그대로 보인다
   *       → GTA5 카드가 아래로 붙어 **상단 라운드에 안 닿는다**(각짐)
   * ```
   *
   * 그래서 **증상 둘이 하나의 원인**이다. 빈 영역을 없애면 둘 다 사라진다.
   *
   * ## ★판 이력 — **이 블록이 유일한 이력 소유자다**
   *
   * 네 판을 거쳤고, 판마다 **다른 자리**를 고쳤다. 그래서 옛 판의 설명이 그 자리에
   * 남으면 코드와 어긋난다 — 실제로 세 자리가 그렇게 남아 #5738 R1 🔵 i1 이 됐다.
   * 이력은 여기만 두고, 각 자리 주석은 **현재 소유자**만 적는다.
   *
   * ```
   *   1판  트랙 `items-end`        짧은 슬라이드를 아래로 → **위가 비고** 잔상 잔존
   *   2판  뷰포트 높이 강제          트랙이 여전히 stretch → 긴 쪽이 잘림(267 → 206)
   *   3판  트랙 `items-stretch`     슬라이드를 늘려 자기 배경으로 칠함
   *                                → 트랙 높이가 **최대 슬라이드**인 것은 그대로(차이 207)
   *   4판  트랙 `items-start`       각 슬라이드가 **자기 높이** + 뷰포트를 활성에 맞춤
   *        + `ResizeObserver`      → 늘릴 것도 잘릴 것도 없다  ← **지금**
   * ```
   *
   * ★2판이 잘린 이유와 4판이 안 잘리는 이유가 갈리는 지점은 **트랙의 정렬**이다.
   * `items-start` 면 자식 높이가 부모에 의존하지 않으므로 뷰포트를 줄여도 슬라이드가
   * 줄지 않고, `ResizeObserver` 에 되먹임 루프도 생기지 않는다.
   *
   * ⚠️ `ResizeObserver` 로 재는 이유: 카드 안 이미지·영상이 늦게 로드되면 높이가
   * 바뀐다. 마운트 시 한 번만 재면 그때 값에 굳는다.
   */
  const trackRef = useRef<HTMLDivElement>(null);
  const [shellHeight, setShellHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      const active = track.children[current] as HTMLElement | undefined;
      if (active) setShellHeight(active.getBoundingClientRect().height);
    };
    measure();
    const ro = new ResizeObserver(measure);
    [...track.children].forEach((c) => ro.observe(c));
    return () => ro.disconnect();
  }, [current, slides.length]);

  useEffect(() => {
    if (!api) return;
    const sync = () => {
      const i = api.selectedScrollSnap();
      setCurrent(i);
      onSelect(i);
    };
    sync();
    api.on("select", sync);
    return () => {
      api.off("select", sync);
    };
  }, [api, onSelect]);

  useEffect(() => {
    if (!autoplay || paused || reducedMotion || !api) return;
    const id = setInterval(() => api.scrollNext(), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [autoplay, paused, reducedMotion, api]);

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
    >
      <div
        className="relative max-h-[70vh] min-h-0 flex-1 overflow-y-auto transition-[height] duration-300"
        style={shellHeight ? { height: shellHeight } : undefined}
      >
        <Carousel
          setApi={setApi}
          opts={{ loop: autoplay, watchDrag: autoplay }}
          className="w-full"
        >
          {/**
           * ★**`items-start` — 각 슬라이드가 자기 높이를 갖는다.**
           *
           * 트랙은 `display:flex` 라 기본이 `stretch` 고, 그러면 모든 슬라이드가
           * **가장 높은 것**의 높이로 늘어난다. 짧은 슬라이드를 보고 있으면 그 차이가
           * 그대로 빈 영역이 된다(실측 3장: 슬라이드 490 ≠ 카드 283 → 207px).
           *
           * `items-start` 로 풀면 슬라이드 높이가 **부모와 무연**해지고, 위 `shellHeight`
           * 가 활성 슬라이드를 재서 뷰포트를 맞춘다. 그래서 늘릴 것도 잘릴 것도 없다.
           *
           * ⚠️ **되먹임 루프가 없는 이유가 여기 있다** — 자식이 부모 높이에 의존하지
           * 않으므로 `ResizeObserver` → `setShellHeight` → 자식 리사이즈로 안 돈다.
           * 정렬을 `stretch` 로 되돌리면 그 무연성이 깨진다(가드가 잠근다).
           *
           * 판별 이력(`items-end` · `items-stretch` 를 왜 버렸나)은 위 `shellHeight`
           * 블록의 「판 이력」이 유일한 소유자다 — 여기 중복해 적지 않는다.
           */}
          <CarouselContent
            ref={trackRef}
            data-popup-track
            className="ml-0 items-start"
          >
            {slides.map((sl) => (
              /**
               * ★**이 요소는 `pl-0` 만 갖는다** — 라운드는 **바깥 래퍼**가 든다.
               *
               * 래퍼 둘이 `overflow-hidden` + 라운드를 함께 갖고 있어서(데스크탑은
               * `rounded-2xl shadow-2xl`, 모바일 시트는 `rounded-t-2xl`) 그 모서리에
               * 닿는 자식은 **슬라이드가 아무것도 안 해도 깎인다.**
               *
               * ⚠️ 실측 2026-09-14 (종전 2026-09-11 기록은 #5756 이후로 낡았다):
               *
               * ```
               *   GTA5    루트 `relative flex h-[190px] items-center bg-black p-4` — 라운드 없음
               *   쿠폰    루트 `… overflow-hidden bg-gradient-to-b …`            — 라운드 없음
               * ```
               *
               * **둘 다 라운드를 안 든다** — 래퍼가 바깥에서 깎는다(가드가 "쿠폰 밴드 자체
               * 라운드: false" 를 잠근다). 쿠폰의 `overflow-hidden` 은 라운드와 무관하다 —
               * 티켓 그래픽이 밴드를 세로로 넘겨서 **자기 넘침을 자르는** 용도다(#5762).
               *
               * ★**여기(트랙 셀)에는 걸지 않는다.** 캐러셀 셀은 트랙 소유라서
               * `overflow-hidden` 을 주면 슬라이드 배경이 셀 경계에 잘리고, 라운드를
               * 주면 래퍼의 라운드와 **두 겹**이 된다.
               *
               * 아래를 안 깎는 이유: 하단 바(「오늘 안 보기」·닫기)와 맞닿아 있어
               * 둥글면 그 사이로 배경이 비친다. 아래는 래퍼만 깎는다.
               */
              <CarouselItem
                key={sl.key}
                className="pl-0"
                // 슬라이드 안 링크(CTA) 클릭 = select_promotion(D1). 노출은 셸이 센다.
                onClickCapture={(e) => {
                  if (isPromotionLinkClick(e.target)) {
                    analyticsInstance?.logSelectPromotion({
                      id: sl.key,
                      slot: "home_popup",
                    });
                  }
                }}
              >
                {sl.node}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {/**
         * ★**페이저 `1/2`** — 시안 `30835:157707` (2026-09-11).
         *
         * 점(아래)과 **역할이 다르다**: 점은 「몇 장이고 지금 어디」를 *누를 수 있게*
         * 보여주고, 페이저는 이미지 위에서 **숫자로** 같은 것을 말한다. 시안이 둘 다
         * 준다 — 이미지가 밝으면 6px 점이 안 보이기 때문으로 읽힌다.
         *
         * ⚠️ **2장 이상일 때만**이다. 단일 슬라이드 판(`30788:157560`)에는 페이저도
         * 점도 없다(그 보드에서 `carousel-dots` 가 `hidden="true"` 다).
         *
         * ⚠️ 껍데기가 소유한다 — 슬라이드 안에 두면 슬라이드마다 「전체 몇 장인지」를
         * 알아야 하고, 제공자가 그걸 모른다.
         */}
        {autoplay && (
          <div
            aria-hidden
            className="pointer-events-none absolute right-4 top-4 flex h-5 w-10 items-center justify-center rounded-[20px] bg-coolgray-100/60 text-xs font-500 leading-4 tracking-[0.5px]"
          >
            <span className="text-white">{current + 1}</span>
            <span className="text-coolgray-20">/{slides.length}</span>
          </div>
        )}
      </div>
      {autoplay && (
        <div className="flex items-center justify-center gap-1.5 bg-white pb-0.5 pt-2.5">
          {slides.map((sl, i) => (
            <button
              key={sl.key}
              type="button"
              aria-label={sl.label}
              aria-current={i === current}
              onClick={() => api?.scrollTo(i)}
              className={cn(
                "size-1.5 rounded-full transition-colors",
                /**
                 * ★색은 **시안 SVG 의 채움값**이다 — `30835:157713`(활성)
                 * `#21272A` · `30835:157714`(비활성) `#C1C7CD` (2026-09-11 실측).
                 *
                 * ⚠️ 종전 활성색이 `bg-blue-60` 이었다. 이 팝업의 CTA 가 파랑이라
                 * 맞아 보였지만 **시안을 안 뜬 값**이었고, 작성자가 잡았다.
                 * 점 행 배경이 흰색이라 어두운 회색이 대비가 더 크다.
                 */
                i === current ? "bg-coolgray-90" : "bg-coolgray-30",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
